import type { CSSProperties } from 'react';
import { Tag, TagGroup, TagList } from '@launchpad-ui/components';
import { BadgeIcon, Icon } from '@launchpad-ui/icons';

import { recordVisit } from '../lib/recentApps';
import type { AppEntry, AppStatus, BadgeVariant } from '../registry';

import styles from './AppTile.module.css';

const STATUS_VARIANT: Record<AppStatus, 'new' | 'info' | 'success'> = {
  new: 'new',
  stable: 'info',
  graduated: 'success',
};

const STATUS_LABEL: Record<AppStatus, string> = {
  new: 'New',
  stable: 'Stable',
  graduated: 'Graduated',
};

// Glow color for each badge variant, keyed to the actual LaunchPad brand token the
// BadgeIcon renders (see launchpad-ui/packages/icons/src/BadgeIcon.tsx and
// styles/BadgeIcon.module.css). yellow and green both render as lime.
const BADGE_GLOW: Record<BadgeVariant, string> = {
  blue: 'var(--lp-color-brand-blue-light)',
  cyan: 'var(--lp-color-brand-cyan-base)',
  purple: 'var(--lp-color-brand-purple-base)',
  pink: 'var(--lp-color-brand-pink-base)',
  orange: 'var(--lp-color-brand-orange-base)',
  yellow: 'var(--lp-color-brand-lime-base)',
  green: 'var(--lp-color-brand-lime-base)',
  'gradient-1': 'var(--lp-color-brand-lime-base)',
  'gradient-2': 'var(--lp-color-brand-pink-base)',
  'gradient-3': 'var(--lp-color-brand-purple-base)',
  'gradient-4': 'var(--lp-color-brand-blue-light)',
  'gradient-5': 'var(--lp-color-brand-purple-base)',
  'gradient-6': 'var(--lp-color-brand-pink-base)',
  'gradient-7': 'var(--lp-color-brand-lime-base)',
};

interface AppTileProps {
  app: AppEntry;
  tabIndex?: number;
  onFocus?: () => void;
  index?: number;
}

export function AppTile({ app, tabIndex, onFocus, index = 0 }: AppTileProps) {
  return (
    <a
      className={styles.tile}
      href={app.href}
      target={app.status === 'graduated' ? '_blank' : undefined}
      rel={app.status === 'graduated' ? 'noopener noreferrer' : undefined}
      tabIndex={tabIndex}
      onFocus={onFocus}
      onClick={() => recordVisit(app.id)}
      style={
        {
          '--animation-delay': `${index * 60}ms`,
          '--glow-color': BADGE_GLOW[app.badgeVariant],
        } as CSSProperties
      }
    >
      <div className={styles.header}>
        <BadgeIcon variant={app.badgeVariant} size="medium">
          <Icon name={app.iconName} />
        </BadgeIcon>
        <TagGroup aria-label="App status">
          <TagList>
            <Tag
              variant={STATUS_VARIANT[app.status]}
              size="small"
              className={app.status === 'new' ? styles.badgePulse : undefined}
            >
              {STATUS_LABEL[app.status]}
            </Tag>
          </TagList>
        </TagGroup>
      </div>
      <p className={styles.name}>{app.name}</p>
      <p className={styles.description}>{app.description}</p>
    </a>
  );
}
